import { joiResolver } from "@hookform/resolvers/joi";
import { jwtDecode } from "jwt-decode";
import React, { useEffect, useState } from 'react';
import { useForm } from "react-hook-form";
import InputMask from "react-input-mask";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { animateScroll as scroll } from "react-scroll";
import { formatPhoneNumber } from "../../constants/formatPhoneNumber";
import { getHttpErrorMessageByStatus } from "../../constants/getHttpErrorMessageByStatus";
import { MAIN_ROUTES } from "../../routing/main_routes";
import { cleaningRequestService } from "../../services/cleaningRequest.service";
import { userService } from "../../services/user.service";
import { UIActions } from "../../store/slices/UISlice";
import { cleaningRequestsActions } from "../../store/slices/cleaningRequestsSlice";
import { qrCodeActions } from "../../store/slices/qrCodeSlice";
import { stepperInfoActions } from "../../store/slices/stepperInfoSlice";
import { userDetailsActions } from "../../store/slices/userDetailsSlice";
import { buildCleaningAddress } from "../../utils/buildCleaningAddress";
import { urlsToFiles } from "../../utils/urlsToFiles";
import { newCustomerValidator } from "../../validators/newCustomer.validator";
import ModalCreatedOrder from "../ModalCreatedOrder/ModalCreatedOrder";
import styles from './NewCustomer.module.scss';

const NewCustomer = ({error, setError}) => {
    const [isModalCreatedOrderOpen, setIsModalCreatedOrderOpen] = useState(false);
    const dispatch = useDispatch();

    const stepperInfo = useSelector(state => state.stepperInfo);

    const {
        service_package,
        countPanoramicWindows: pano_window_count,
        countOrdinaryWindows: reg_window_count,
        typeCleaning: cleaning_type,
        squareApartment: area,
        countRooms: room_count,
        countBathrooms: wc_count,
        accessToApartment: present_at_home,
        locationAccessToApartment: present_at_home_desc,
        services,
        request_datetime,
        request_files,
        comment,
        request_price,
        promo_code
    } = stepperInfo;
    const {photos} = useSelector(state => state.qrCode);

    const {
        register,
        handleSubmit,
        formState: {errors, isValid},
        setError: setErrorForm,
      watch
    } = useForm({
        resolver: joiResolver(newCustomerValidator),
        mode: 'all',
    });

    const mobile_nummer = watch("mobile_nummer");

    const turnReceivedImgUrlsToFiles = (photoUrls) => urlsToFiles(photoUrls);

    const handleCreateFormData = (photos, cleaning_request_data, qrCodePhotos) => {
        let formData = new FormData();

        if (photos?.length > 0) {
            for (const photo of photos) {
                formData.append('photos_data', photo);
            }
        }

        if (qrCodePhotos?.length > 0) {
            for (const photo of qrCodePhotos) {
                formData.append('photos_data', photo);
            }
        }

        formData.append('cleaning_request_related_data', JSON.stringify(cleaning_request_data));

        return formData;
    };

    const handleCreateOrder = async (data) => {
        if (mobile_nummer?.includes("_")) {
            setErrorForm("mobile_nummer", {message: "Неправильний номер телефону"});
        }
        const user = {
            ...data,
            mobile_nummer: formatPhoneNumber(data.mobile_nummer),
        };

        // create new user
        try {
            dispatch(UIActions.setIsLoading(true));

            await userService.create(user);
        } catch (error) {
            const errorMessage = getHttpErrorMessageByStatus(error);
            setError(errorMessage);
            // setError(JSON.stringify(error?.response?.data));
            return;
        } finally {
            dispatch(UIActions.setIsLoading(false));
        }

        // login
        try {
            dispatch(UIActions.setIsLoading(true));

            const {data: tokens} = await userService.login(data);
            const {cleaner_id, user_id, iat, exp} = jwtDecode(tokens.access);

            if (!cleaner_id) {
                // Store tokens before any authenticated request so the axios
                // interceptor can attach the Authorization header.
                localStorage.setItem('isCleaner', 'false');
                localStorage.setItem('token', JSON.stringify(tokens));
                localStorage.setItem('expires', JSON.stringify({iat, exp}));

                await dispatch(userDetailsActions.getById({id: user_id}));

                try {
                    const servicesIds = services.map((service) => service.id);

                    const cleaning_request = {
                        ...buildCleaningAddress(stepperInfo),
                        cleaning_type: cleaning_type || 'Генеральне',
                        area,
                        services: servicesIds,
                        present_at_home,
                        present_at_home_desc: present_at_home_desc || null,
                        room_count: room_count || null,
                        wc_count: wc_count || null,
                        pano_window_count: pano_window_count || null,
                        reg_window_count: reg_window_count || null,
                        comment,
                        request_price,
                        promo_code: promo_code || null,
                        request_datetime,
                        service_package: service_package.id,
                    };

                    const files = await turnReceivedImgUrlsToFiles(photos);

                    const formData = handleCreateFormData(request_files, cleaning_request, files);

                    await cleaningRequestService.create(formData);
                    navigate(MAIN_ROUTES.MAIN);

                    dispatch(stepperInfoActions.clearAllData());

                    dispatch(cleaningRequestsActions.setStatus('success'));
                } catch (error) {
                    dispatch(stepperInfoActions.clearAllData());
                    dispatch(qrCodeActions.clearAllData());
                    setError(JSON.stringify(error?.response?.data));
                } finally {
                    dispatch(UIActions.setIsLoading(false));
                }
            }
        } catch (error) {
            setError(JSON.stringify(error?.response?.data));
        } finally {
            dispatch(UIActions.setIsLoading(false));
            dispatch(stepperInfoActions.clearAllData());
            dispatch(qrCodeActions.clearAllData());
        }
    }

    const onError = (errors, e) => {
        if (mobile_nummer?.includes("_")) {
            setErrorForm("mobile_nummer", {message: "Неправильний номер телефону"});
        }
        scroll.scrollToTop();
    };

    useEffect(() => {
        if (mobile_nummer?.includes("_")) {
            setErrorForm("mobile_nummer", {message: "Неправильний номер телефону"});
        }
    }, [mobile_nummer]);

    const navigate = useNavigate();

    return (
        <div className={styles.newCustomer}>
            <form
                autoComplete='off'
                onSubmit={handleSubmit(handleCreateOrder, onError)}
                action="#"
                className={styles.newCustomer_form}
            >
                <div className={styles.newCustomer_form_fullName}>
                    <div className={styles.newCustomer_form_fullName_block}>
                        <label>
                            <span>Прізвище <sup>*</sup></span>
                            <input
                              style={{
                                  borderColor: errors?.last_name && "#F04438"
                              }}
                                {...register('last_name')}
                                className={styles.input}
                                type="text"
                                // placeholder={'напр. Бойко'}
                                autoComplete='new_last_name'
                            />
                        </label>
                        {errors.last_name && <span className={styles.error}> {errors.last_name.message}</span>}
                    </div>

                    <div className={styles.newCustomer_form_fullName_block}>
                        <label>
                            <span>Ім’я <sup>*</sup></span>
                            <input
                              style={{
                                  borderColor: errors?.first_name && "#F04438"
                              }}
                                {...register('first_name')}
                                className={styles.input}
                                type="text"
                                // placeholder={'напр. Василь'}
                                autoComplete='new_first_name'
                            />
                        </label>
                        {errors.first_name && <span className={styles.error}> {errors.first_name.message}</span>}
                    </div>
                </div>

                <div className={styles.newCustomer_form_block}>
                    <label>
                        <span>Email <sup>*</sup></span>
                        <input
                          style={{
                              borderColor: errors?.email && "#F04438"
                          }}
                            {...register('email')}
                            className={styles.input}
                            type="text"
                            // placeholder={'напр. vasyl@gmail.com'}
                            autoComplete='new_email'
                        />
                    </label>
                    {errors.email && <span className={styles.error}> {errors.email.message}</span>}
                </div>

                <div className={styles.newCustomer_form_block}>
                    <label>
                        <span>Номер телефону <sup>*</sup></span>
                        {/*<input*/}
                        {/*    {...register('phone')}*/}
                        {/*    className={styles.input}*/}
                        {/*    type="text"*/}
                        {/*    placeholder={'+1 (555) 000-0000'}*/}
                        {/*/>*/}
                        <InputMask
                            className={`${styles.input} ${errors?.mobile_nummer ? styles.error : ""}`}
                            {...register('mobile_nummer')}
                            mask="+38 (099) 999-99-99"
                            placeholder="+38 (0XX) XXX-XX-XX"
                        />
                    </label>
                    {errors.mobile_nummer && <span className={styles.error}> {errors.mobile_nummer.message}</span>}
                </div>

                <div className={styles.newCustomer_form_block}>
                    <label>
                        <span>Пароль <sup>*</sup></span>
                        <input
                          style={{
                              borderColor: errors?.password && "#F04438"
                          }}
                            {...register('password')}
                            className={styles.input}
                            type="password"
                            autoComplete='new_password'
                        />
                    </label>
                    {errors.password && <span className={styles.error}> {errors.password.message}</span>}
                </div>

                <div className={styles.newCustomer_form_block}>
                    <label>
                        <span>Повторити пароль <sup>*</sup></span>
                        <input
                          style={{
                              borderColor: errors?.confirm_password && "#F04438"
                          }}
                            {...register('confirm_password')}
                            className={styles.input}
                            type="password"
                            autoComplete='new_confirm_password'
                        />
                    </label>
                    {errors.confirm_password &&
                        <span className={styles.error}> {errors.confirm_password.message}</span>}
                    {error && <span className={styles.error}>{error}</span>}
                </div>

                <button
                    // onClick={handleCreateOrder}
                    // disabled={!isValid}
                    className={styles.button}
                    type={'submit'}
                >
                </button>

                {isModalCreatedOrderOpen && <ModalCreatedOrder/>}
            </form>
        </div>
    );
};

export default NewCustomer;
